import { Action, combineReducers } from "redux";

import tokenReducer from "./tokenReducer";
import convoReducer, { ReduxConversation } from "./convoReducer";
import sidReducer from "./currentConvoReducer";
import messageReducer, { ChatMessagesState } from "./messageListReducer";
import loadingReducer from "./loadingReducer";
import participantReducer, { ParticipantsType } from "./participantsReducer";
import userReducer, { UsersState } from "./userReducer";
import unreadMessagesReducer, {
  UnreadMessagesState,
} from "./unreadMessagesReducer";
import attachmentsReducer, { AttachmentsState } from "./attachmentsReducer";
import { ActionType } from "../action-types";
import typingDataReducer, { TypingDataState } from "./typingDataReducer";
import lastReadIndexReducer from "./lastReadIndexReducer";
import notificationsReducer, {
  NotificationsType,
} from "./notificationsReducer";
import timeReducer from "./timeReducer";
import localReducer from "./localReducer";
import twilioReducer, { TwilioAuthState } from "./twilioReducer";
import conversationsReducer, {
  ConversationsState,
} from "./conversationsReducer";
import operatorReducer from "./operatorReducer";
import { OperatorDto } from "../../dto/postLogin";
import { RolesDto } from "../../dto/roles";
import rolesReducer from "./rolesReducer";
import { TemplateDto } from "../../dto/template";
import { templatesReducer } from "./templatesReducer";
import { templateSendPendingBySidReducer } from "./templateSendPendingBySidReducer";
import {
  customerSearchReducer,
  CustomerSearchState,
} from "./customerSearchReducer";
import conversationActionReducer, {
  conversationActionInitialState,
  ConversationActionState,
} from "./conversationActionsReducer";
import operatorsReducer, {
  operatorsInitialState,
  OperatorsState,
} from "./operatorsReducer";
import {
  signalrInitialState,
  signalrReducer,
  SignalRState,
} from "./signalrReducer";
import {
  contactConversationInitialState,
  contactConversationReducer,
  ContactConversationState,
} from "./contactConversationReducer";
import { UnlockReasonsState } from "../../dto/unlockReasons";
import unlockReasonsReducer from "./unlockReasonsReducer";

export type AppState = {
  token: string;
  twilio: TwilioAuthState;
  convos: ReduxConversation[];
  sid: string;
  messages: ChatMessagesState;
  unreadMessages: UnreadMessagesState;
  loadingStatus: boolean;
  participants: ParticipantsType;
  users: UsersState;
  attachments: AttachmentsState;
  typingData: TypingDataState;
  lastReadIndex: number;
  notifications: NotificationsType;
  use24hTimeFormat: boolean;
  local: string;
  conversations: ConversationsState;
  operator: OperatorDto | null;
  roles: RolesDto[];
  templates: TemplateDto[];
  templateSendPendingBySid: Record<string, boolean>;
  customerSearch: CustomerSearchState;
  operators: OperatorsState;
  conversationAction: ConversationActionState;
  signalr: SignalRState;
  contactConversation: ContactConversationState;
  unlockReasons: UnlockReasonsState;
};

export const initialState = {
  token: "",
  twilio: { token: "", identity: "" },
  sid: "",
  messages: {},
  attachments: {},
  participants: {},
  users: {},
  convos: [],
  unreadMessages: {},
  loadingStatus: true,
  typingData: {},
  lastReadIndex: -1,
  notifications: [],
  use24hTimeFormat: false,
  local: "",
  conversations: {
    items: [],
    query: "",
    page: 1,
    pageSize: 25,
    totalItems: 0,
  },
  operator: null,
  roles: [],
  templates: [] as TemplateDto[],
  templateSendPendingBySid: {} as Record<string, boolean>,
  customerSearch: {
    item: null,
    isLoading: false,
    query: "",
  } as CustomerSearchState,
  operators: operatorsInitialState,
  conversationAction: conversationActionInitialState,
  signalr: signalrInitialState,
  contactConversation: contactConversationInitialState,
  unlockReasons: { reasons: [] },
};

const reducers = (
  state: AppState | undefined,
  action: Action
): ReturnType<typeof appReducer> => {
  if (action.type === ActionType.LOGOUT) {
    return appReducer(initialState, action);
  }

  return appReducer(state, action);
};

const appReducer = combineReducers({
  token: tokenReducer,
  twilio: twilioReducer,
  convos: convoReducer,
  sid: sidReducer,
  lastReadIndex: lastReadIndexReducer,
  messages: messageReducer,
  loadingStatus: loadingReducer,
  participants: participantReducer,
  users: userReducer,
  unreadMessages: unreadMessagesReducer,
  attachments: attachmentsReducer,
  typingData: typingDataReducer,
  notifications: notificationsReducer,
  templates: templatesReducer,
  use24hTimeFormat: timeReducer,
  local: localReducer,
  conversations: conversationsReducer,
  operator: operatorReducer,
  roles: rolesReducer,
  templateSendPendingBySid: templateSendPendingBySidReducer,
  customerSearch: customerSearchReducer,
  conversationAction: conversationActionReducer,
  operators: operatorsReducer,
  signalr: signalrReducer,
  contactConversation: contactConversationReducer,
  unlockReasons: unlockReasonsReducer,
});

export default reducers;
